<script setup lang="ts">
import { useField } from 'vee-validate'

const originalProps = defineProps<{
  name: string
  label?: string
  tooltip?: any
  items?: any[]
  hiddenItems?: any[]
  button?: boolean
  hiddenItemsTextExpand?: string
  hiddenItemsTextCollapse?: string
  disableStoryblok?: boolean
  rowLength?: string
  isOpen?: boolean
}>()

const emit = defineEmits(['changed'])
const ga4 = useAnalytics()
const props = originalProps.disableStoryblok ? originalProps : useStoryblokProps(originalProps)
const { value: modelValue, errorMessage } = useField<boolean | string>(props.name, undefined, {
  type: 'radio',
})
const selectedLabel = computed(() => props.items?.find(item => item.value === modelValue.value)?.description)
const rowLength = `grid-cols-${props?.rowLength || 3}`

const isOpen = ref(props.isOpen || false)
function clickEvent(selectedValue: string) {
  modelValue.value = selectedValue
  emit('changed', selectedValue)
  ga4.trackEvent({
    event_action: 'radio',
    event_label: originalProps?.name,
  })
}

watch(errorMessage, () => {
  if (errorMessage.value) ga4.errorEvent({ event_label: originalProps?.name })
})
</script>

<template>
  <div class="flex flex-col">
    <div class="xs:flex xs:gap-2.5 xs:items-center">
      <BaseLabel v-if="props.label" :tooltip="props.tooltip">
        {{ props.label }}
      </BaseLabel>
      <slot name="hint" />
    </div>
    <div v-if="selectedLabel" class="pb-2.5 pt-1.25 text-gray-dark">
      {{ selectedLabel }}
    </div>
    <div
      class="grid gap-2.5"
      :class="[rowLength]"
    >
      <div
        v-for="item in props.items"
        :key="item.value"
        :data-test-id="`FieldRadioButtons|${name}|${item.value}`"
        :class="{
          'border-2 border-primary bg-primary/15': modelValue === item.value,
          'border-1 border-form': modelValue !== item.value,
        }"
        class="shadow-form rounded-xl group cursor-pointer hover:border-hover"
        @click="clickEvent(item.value)"
      >
        <div
          :class="{
            'border-1 border-transparent': modelValue !== item.value,
          }"
          class="w-full h-full flex flex-col items-center justify-center p-2.5"
        >
          <AppIcon v-if="item?.icon?.icon" v-bind="item.icon" size="20" class="text-primary" />
          <span
            class="text-xs font-medium text-center  sm:text-small"
          >
            {{ item.text || item.label }}</span>
        </div>
      </div>
    </div>
    <CollapseTransition>
      <div v-if="isOpen" class="grid grid-cols-3 gap-2.5 pt-2.5">
        <div
          v-for="{ text, value, icon } in props.hiddenItems"
          :key="value"
          :data-test-id="`FieldRadioButtons|${name}|${value}`"
          class="border-1 p-2.5 rounded-xl group cursor-pointer flex flex-col justify-center items-center hover:border-hover"
          :class="{ '!border-2 border-primary p-9px bg-primary/15': modelValue === value }"
          @click="clickEvent(value)"
        >
          <AppIcon v-if="icon.icon" v-bind="icon" size="20" class="text-primary" />
          <span class="text-xs font-medium text-center sm:text-small" v-html="text || label" />
        </div>
      </div>
    </CollapseTransition>
    <BaseButton v-if="props.hiddenItems?.length" :test-id="`BaseButton|${name}`" class="mr-auto mt-2.5" render="text" @click="isOpen = !isOpen">
      <span class="mr-2">{{ isOpen ? props.hiddenItemsTextCollapse : props.hiddenItemsTextExpand }}</span>
      <AppIcon class="transform duration-300" :class="{ 'rotate-180': isOpen }" icon="far-angle-down" />
    </BaseButton>
    <FormFieldErrorMessage v-if="errorMessage" class="pt-2">
      {{ errorMessage }}
    </FormFieldErrorMessage>
  </div>
</template>
